import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  Eye,
  Loader2,
  Lock,
  MoreHorizontal,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { AdminUserDetailModal } from "@/components/admin/admin-user-detail-modal";
import { authClient } from "@/lib/auth-client";
import { client } from "@/utils/orpc";
import { Avatar, AvatarFallback, AvatarImage } from "@super-resume-new/ui/components/avatar";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Card, CardContent } from "@super-resume-new/ui/components/card";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@super-resume-new/ui/components/dialog";
import { Input } from "@super-resume-new/ui/components/input";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "@super-resume-new/ui/components/menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@super-resume-new/ui/components/table";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/users")({
  head: () =>
    seo({
      title: "User Management | JobCues Admin",
      description: "Moderate users, grant credits, inspect candidate dossiers, and manage permissions.",
      path: "/admin/users",
      noindex: true,
    }),
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | "admin" | "user">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "banned">("all");
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState(10);
  const [creditReason, setCreditReason] = useState("admin_grant");
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banReason, setBanReason] = useState("Violation of terms");

  // Query users via optimized backend listUsers endpoint
  const {
    data: usersData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["admin", "users", page, searchValue, roleFilter, statusFilter],
    queryFn: () =>
      client.admin.listUsers({
        page,
        limit: pageSize,
        query: searchValue.trim() || undefined,
        role: roleFilter,
        status: statusFilter,
      }),
  });

  // Grant Credits Mutation
  const grantCreditsMutation = useMutation({
    mutationFn: async ({
      userId,
      amount,
      reason,
    }: {
      userId: string;
      amount: number;
      reason: string;
    }) => {
      return client.admin.grantUserCredits({ userId, amount, reason });
    },
    onSuccess: () => {
      toast.success(`Granted ${creditAmount} credits successfully`);
      setCreditDialogOpen(false);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to grant credits");
    },
  });

  // Role toggle mutation
  const setRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: "admin" | "user" }) => {
      return authClient.admin.setRole({ userId, role });
    },
    onSuccess: () => {
      toast.success("User role updated");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update role");
    },
  });

  // Ban mutation
  const banUserMutation = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
      return authClient.admin.banUser({ userId, banReason: reason });
    },
    onSuccess: () => {
      toast.success("User banned");
      setBanDialogOpen(false);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to ban user");
    },
  });

  // Unban mutation
  const unbanUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return authClient.admin.unbanUser({ userId });
    },
    onSuccess: () => {
      toast.success("User unbanned");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to unban user");
    },
  });

  // Impersonate mutation
  const impersonateMutation = useMutation({
    mutationFn: async (userId: string) => {
      return authClient.admin.impersonateUser({ userId });
    },
    onSuccess: () => {
      toast.success("Now impersonating user session. Redirecting…");
      window.location.href = "/";
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to impersonate");
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return authClient.admin.removeUser({ userId });
    },
    onSuccess: () => {
      toast.success("User removed");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to remove user");
    },
  });

  const totalUsers = usersData?.total ?? 0;
  const totalPages = usersData?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex flex-1 items-center gap-2 w-full max-w-lg">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name, email, or ID…"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setPage(1);
              }}
              className="pl-9 text-xs"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e: any) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border bg-card px-2 text-xs text-foreground font-medium outline-none"
          >
            <option value="all">All Roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e: any) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border bg-card px-2 text-xs text-foreground font-medium outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="banned">Banned</option>
          </select>
        </div>

        <div className="text-xs text-muted-foreground tabular-nums">
          Showing {usersData?.users?.length ?? 0} of {totalUsers} registered users
        </div>
      </div>

      {/* Users Table */}
      <Card className="border-border bg-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User Account</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Credits</TableHead>
                <TableHead>Tool Activity</TableHead>
                <TableHead>Joined Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                    <Loader2 className="mx-auto size-6 animate-spin text-brand mb-2" />
                    Loading user catalog…
                  </TableCell>
                </TableRow>
              ) : usersData?.users?.length ? (
                usersData.users.map((u: any) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <Avatar
                          className="size-8 cursor-pointer hover:ring-2 hover:ring-brand/40 transition"
                          onClick={() => setDetailUserId(u.id)}
                        >
                          <AvatarImage src={u.image} />
                          <AvatarFallback className="text-xs font-semibold">
                            {u.name?.[0] || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => setDetailUserId(u.id)}
                            className="text-left font-semibold text-foreground hover:text-brand hover:underline transition-colors cursor-pointer text-xs"
                          >
                            {u.name || "Anonymous"}
                          </button>
                          <span className="text-xs text-muted-foreground font-mono">{u.email}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          u.role === "admin"
                            ? "bg-brand/10 text-brand-foreground border-brand/30 font-semibold"
                            : "bg-muted text-muted-foreground"
                        }
                      >
                        {u.role || "user"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {u.banned ? (
                        <Badge
                          variant="outline"
                          className="bg-destructive/10 text-destructive border-destructive/30"
                        >
                          Banned
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30"
                        >
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="font-mono text-xs font-semibold bg-amber-500/10 text-amber-500 border-amber-500/20"
                      >
                        {u.quickStats?.balance ?? 0} cr
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1 max-w-[200px]">
                        {(u.quickStats?.tailored ?? 0) > 0 ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 bg-brand/5 border-brand/20 text-brand-foreground"
                          >
                            {u.quickStats?.tailored} tailored
                          </Badge>
                        ) : null}
                        {(u.quickStats?.ats ?? 0) > 0 ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 bg-violet-500/5 border-violet-500/20 text-violet-500"
                          >
                            {u.quickStats?.ats} ATS
                          </Badge>
                        ) : null}
                        {(u.quickStats?.matches ?? 0) > 0 ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 bg-emerald-500/5 border-emerald-500/20 text-emerald-500"
                          >
                            {u.quickStats?.matches} matches
                          </Badge>
                        ) : null}
                        {!u.quickStats?.tailored &&
                          !u.quickStats?.ats &&
                          !u.quickStats?.matches && (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDetailUserId(u.id)}
                          className="h-8 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground hover:border-brand/40"
                          title="View Full Profile & Tools Info"
                        >
                          <Eye className="size-3.5" />
                          <span className="hidden xl:inline">Full Info</span>
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          title="Grant Credits"
                          onClick={() => {
                            setSelectedUser(u);
                            setCreditDialogOpen(true);
                          }}
                          className="size-8 p-0 text-amber-500 hover:text-amber-500 hover:bg-amber-500/10"
                        >
                          <Coins className="size-3.5" />
                        </Button>

                        <Menu>
                          <MenuTrigger
                            render={
                              <button
                                type="button"
                                className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-background p-1 text-muted-foreground hover:text-foreground cursor-pointer"
                              />
                            }
                          >
                            <MoreHorizontal className="size-3.5" />
                          </MenuTrigger>
                          <MenuPopup align="end" className="w-48">
                            {u.role === "admin" ? (
                              <MenuItem
                                onClick={() =>
                                  setRoleMutation.mutate({ userId: u.id, role: "user" })
                                }
                              >
                                <ShieldCheck className="size-3.5 text-muted-foreground" />
                                Demote to User
                              </MenuItem>
                            ) : (
                              <MenuItem
                                onClick={() =>
                                  setRoleMutation.mutate({ userId: u.id, role: "admin" })
                                }
                              >
                                <ShieldCheck className="size-3.5 text-brand" />
                                Promote to Admin
                              </MenuItem>
                            )}

                            <MenuItem onClick={() => impersonateMutation.mutate(u.id)}>
                              <Eye className="size-3.5" />
                              Impersonate User
                            </MenuItem>

                            <MenuSeparator />

                            {u.banned ? (
                              <MenuItem onClick={() => unbanUserMutation.mutate(u.id)}>
                                <UserCheck className="size-3.5 text-emerald-500" />
                                Unban Account
                              </MenuItem>
                            ) : (
                              <MenuItem
                                onClick={() => {
                                  setSelectedUser(u);
                                  setBanDialogOpen(true);
                                }}
                                className="text-destructive focus:text-destructive"
                              >
                                <Lock className="size-3.5" />
                                Ban Account
                              </MenuItem>
                            )}

                            <MenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => {
                                if (
                                  confirm(
                                    `Are you sure you want to delete user ${u.name || u.email}? This action is irreversible.`,
                                  )
                                ) {
                                  deleteUserMutation.mutate(u.id);
                                }
                              }}
                            >
                              <Trash2 className="size-3.5" />
                              Delete Account
                            </MenuItem>
                          </MenuPopup>
                        </Menu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-muted-foreground text-sm">
                    No users found matching query.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="gap-1"
          >
            <ChevronLeft className="size-3.5" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1"
          >
            Next <ChevronRight className="size-3.5" />
          </Button>
        </div>
      )}

      {/* Grant Credits Dialog */}
      <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
        <DialogPopup className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grant Credits</DialogTitle>
            <DialogDescription>
              Allocate credits directly to{" "}
              <span className="font-semibold text-foreground">
                {selectedUser?.name || selectedUser?.email}
              </span>
              .
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div>
              <label className="text-xs font-medium text-foreground">Amount (Credits)</label>
              <Input
                type="number"
                min="1"
                max="1000"
                value={creditAmount}
                onChange={(e) => setCreditAmount(Number(e.target.value))}
                className="mt-1 text-sm font-mono"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-foreground">Reason</label>
              <select
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-xs text-foreground outline-none"
              >
                <option value="admin_grant">Administrative Allocation</option>
                <option value="support_goodwill">Customer Support Goodwill</option>
                <option value="signup_bonus">Referral / Bonus Adjustment</option>
                <option value="manual_purchase">Manual Payment Processing</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <DialogClose render={<Button variant="outline">Cancel</Button>} />
            <Button
              onClick={() =>
                grantCreditsMutation.mutate({
                  userId: selectedUser.id,
                  amount: creditAmount,
                  reason: creditReason,
                })
              }
              disabled={grantCreditsMutation.isPending}
            >
              {grantCreditsMutation.isPending ? "Granting…" : "Grant Credits"}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      {/* Ban User Dialog */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogPopup className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Ban User Account</DialogTitle>
            <DialogDescription>
              Revoke active sessions and prevent further login for{" "}
              <span className="font-semibold text-foreground">
                {selectedUser?.name || selectedUser?.email}
              </span>
              .
            </DialogDescription>
          </DialogHeader>

          <div className="py-3">
            <label className="text-xs font-medium text-foreground">Reason for suspension</label>
            <Input
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="e.g. Terms of service violation, scraper abuse"
              className="mt-1 text-xs"
            />
          </div>

          <DialogFooter>
            <DialogClose render={<Button variant="outline">Cancel</Button>} />
            <Button
              variant="destructive"
              onClick={() =>
                banUserMutation.mutate({
                  userId: selectedUser.id,
                  reason: banReason,
                })
              }
              disabled={banUserMutation.isPending}
            >
              {banUserMutation.isPending ? "Banning…" : "Confirm Ban"}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      {/* User Dossier & Full Activity Modal */}
      <AdminUserDetailModal
        userId={detailUserId}
        isOpen={Boolean(detailUserId)}
        onClose={() => setDetailUserId(null)}
        onGrantCredits={(u) => {
          setSelectedUser(u);
          setCreditDialogOpen(true);
        }}
        onBanUser={(u) => {
          setSelectedUser(u);
          setBanDialogOpen(true);
        }}
        onUnbanUser={(id) => unbanUserMutation.mutate(id)}
        onImpersonate={(id) => impersonateMutation.mutate(id)}
      />
    </div>
  );
}
